import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useContext,
  useRef,
  useLayoutEffect,
  useState,
  useId,
  type ReactNode,
} from "react";
import cx from "classnames";
import tinycolor from "tinycolor2";

import { Avatar, AvatarProps } from "../../Avatar/internal/Avatar";
import { useAccessibleColor, usePrefersColorScheme } from "../../../hooks";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";

import styles from "../Chip.module.scss";
import { Icon } from "../../Icon";
import { SrOnly } from "../../SrOnly/SrOnly";
import { LayoutUtilProps, Size } from "../../../types";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import { ThemeProviderContext } from "../../../providers/ThemeProvider/internal/ThemeProviderContext";

/**
 * Props for the Chip component
 * @extends ComponentPropsWithoutRef<"span">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type ChipBaseProps = Omit<ComponentPropsWithoutRef<"span">, "prefix"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * The text that displays in the chip.
     */
    label: string;
    /**
     * Color of the Chip
     */
    color?: string;
    /**
     * Wraps text when it overflows
     */
    textWrap?: boolean;
    /**
     * Content displayed in the chip, before the text.
     */
    prefix?: ReactNode;

    /**
     * Image URL to display in an avatar before the chip text.
     */
    avatar?: AvatarProps["image"];

    /**
     * Content displayed in the chip, after the text.
     */
    suffix?: ReactNode;
  };

type ChipClickHandler = (
  e?: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>,
) => void;

type ChipCloseHandler = (
  e: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>,
) => void;

type SmallChipHandlers =
  | {
      /**
       * Callback for when the Chip is clicked
       *
       * @remarks
       * Adding this will make Chip focusable
       */
      onClick?: ChipClickHandler;
      /**
       * Called when the component is closed. If supplied, a close button will be rendered.
       * @returns void
       */
      onClose?: never;
    }
  | {
      /**
       * Called when the component is closed. If supplied, a close button will be rendered.
       * @returns void
       */
      onClose?: ChipCloseHandler;
      /**
       * Callback for when the Chip is clicked
       *
       * @remarks
       * Adding this will make Chip focusable
       */
      onClick?: never;
    };

type SmallChipProps = ChipBaseProps & {
  /**
   * The size of the chip
   * @default medium
   */
  size: Extract<Size, "small">;
} & SmallChipHandlers;

type MediumChipProps = ChipBaseProps & {
  /**
   * The size of the chip
   * @default medium
   */
  size?: Extract<Size, "medium">;
  /**
   * Callback for when the Chip is clicked
   *
   * @remarks
   * Adding this will make Chip focusable
   */
  onClick?: ChipClickHandler;
  /**
   * Called when the component is closed. If supplied, a close button will be rendered.
   * @returns void
   */
  onClose?: ChipCloseHandler;
};

export type ChipProps = SmallChipProps | MediumChipProps;

/**
 * Chip component for displaying compact information or tags.
 *
 * Features:
 * - Configurable sizes (small, medium)
 * - Custom color support with automatic contrast calculation
 * - Optional click handler for interactive chips
 * - Optional close button for removable chips
 * - Text wrapping support for long labels
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation (Enter/Space to click, Delete/Backspace to close)
 * - Screen reader instructions for removal actions
 * - Theme-aware color adaptation
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <Chip
 *   label="JavaScript"
 *   color="#f7df1e"
 *   onClose={(e) => console.log('Chip removed')}
 *   onClick={(e) => console.log('Chip clicked')}
 * />
 */
export const Chip = forwardRef<HTMLSpanElement, ChipProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    color,
    onClose,
    onClick,
    label,
    style,
    textWrap,
    prefix,
    suffix,
    avatar,
    size = "medium",
    tabIndex,
    ...rest
  } = componentProps;

  const innerRef = useRef<HTMLSpanElement>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const descriptionId = useId();

  useLayoutEffect(() => {
    const element = innerRef.current;

    const getSingleLineHeight = (element: HTMLSpanElement | null) => {
      if (avatar == null) return 0;
      if (!element) return 0;
      const computedStyle = getComputedStyle(element);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      return lineHeight + paddingTop + paddingBottom;
    };

    const singleLineHeight = getSingleLineHeight(element);

    const observer = new ResizeObserver(() => {
      if (element && avatar != null) {
        // If actual height is significantly larger than single line, we have multiple lines
        // Using 1.5 threshold to account for rounding
        setIsMultiline(element.clientHeight > singleLineHeight * 1.5);
      }
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [avatar]);

  /*
   * @TODO: below to be updated with localization
   * removeTextIndex is for when removeText comes after the label depending on the language
   */
  const removeText = ", removeable chip";
  const newLabel = `${label}${onClose ? removeText : ""}`;

  const ChipClassNames = cx(styles["chip"], className, {
    [styles["size-small"]]: size === "small",
    [styles["has-close"]]: !!onClose,
    [styles["text-wrap"]]: textWrap,
  });

  const AvatarClassNames = cx(styles["avatar"], {
    [styles["multiline"]]: textWrap && isMultiline,
  });

  const { mode: themeMode } = useContext(ThemeProviderContext);
  const { mode: sysMode } = usePrefersColorScheme();
  const mode = themeMode ?? sysMode;

  const { foreground, background, border } = useAccessibleColor(color, mode);

  const bgObj = tinycolor(background);

  const styleWithColor = {
    "--int-bg-color": color && background,
    "--int-font-color": color && foreground,
    "--int-border-color": color && border,
    "--int-close-background-color": color
      ? bgObj.isDark()
        ? bgObj.lighten(15).toHexString()
        : bgObj.darken(15).toHexString()
      : "#14141429",
    ...style,
    ...layoutStyles,
  };

  const onKeyUpHandler = (e: KeyboardEvent<HTMLSpanElement>) => {
    switch (e.code) {
      case "Delete":
      case "Backspace":
        onClose?.(e);
        break;

      case "Enter":
      case "Space":
        onClick?.(e);
        break;

      default:
        break;
    }
  };

  return (
    <span
      className={ChipClassNames}
      style={styleWithColor}
      data-anv="chip"
      role={onClick ? "button" : undefined}
      {...(onClick || onClose ? { ["aria-label"]: newLabel } : {})}
      {...(onClose ? { ["aria-describedby"]: descriptionId } : {})}
      {...rest}
      ref={ref}
      onClick={onClick}
      onKeyUp={onKeyUpHandler}
      tabIndex={onClick || onClose ? 0 : tabIndex}
    >
      {avatar != null ? (
        <span className={AvatarClassNames}>
          <Avatar
            className={styles.avatarInner}
            name={label}
            color={color}
            image={avatar}
            size={size === "small" ? "xsmall" : "small"}
          />
        </span>
      ) : null}

      {prefix != null ? <span className={styles.prefix}>{prefix}</span> : null}

      <span className={styles.inner} ref={innerRef}>
        {label}
      </span>

      {suffix != null ? <span className={styles.suffix}>{suffix}</span> : null}

      {!!onClose && (
        <>
          <span
            aria-hidden
            className={styles["close"]}
            onClick={(e) => {
              e.stopPropagation();
              onClose(e);
            }}
          >
            <Icon aria-hidden svg={Close} size={size} />
          </span>
          <SrOnly id={descriptionId}>
            Press Delete or Backspace to remove.
          </SrOnly>
        </>
      )}
    </span>
  );
});

Chip.displayName = "Chip";
